import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { HOME_PATH } from "../lib/auth-gate";

export function Login() {
  const { configured, session, signIn, error: authError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (session) {
    return <Navigate to={HOME_PATH} replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    if (!email.trim() || !password) {
      setFormError("Informe e-mail e senha.");
      return;
    }

    setSubmitting(true);
    const result = await signIn(email.trim(), password);
    setSubmitting(false);

    if (result.ok) {
      navigate(HOME_PATH, { replace: true });
    } else {
      setFormError(result.error ?? "Falha ao entrar.");
    }
  };

  return (
    <div className="login-shell">
      <section className="login-intro" aria-label="Tracker Bet Analystic">
        <div className="brand"><span className="brand-mark" aria-hidden="true"><Activity size={18} strokeWidth={1.75} /></span><span>Tracker Bet<small>Analystic</small></span></div>
        <h1>Mais contexto.<br />Melhores análises.</h1>
        <p>Estatísticas de futebol, histórico e comparação de odds em um único espaço de trabalho.</p>
        <p className="mt-6 text-sm">Análise pré-jogo · Modelos experimentais · Histórico privado</p>
      </section>
      <div className="card login-card">
        <header className="mb-6 text-center">
          <p className="eyebrow mb-3">Bem-vindo de volta</p><h2 className="text-2xl font-bold">Acesse seu workspace</h2>
          <p className="text-sm text-[var(--color-text-muted)] mt-2">
            Acesso privado. Não há cadastro público.
          </p>
        </header>

        {!configured && (
          <div className="alert alert-warning mb-4" role="status">
            Supabase não configurado neste ambiente. Configure <code>VITE_SUPABASE_URL</code> e a
            chave publicável para habilitar o login real. O modo demonstração offline continua disponível.
            <div className="mt-3">
              <a href="/dashboard" className="btn btn-secondary">Entrar em modo demonstração</a>
            </div>
          </div>
        )}

        {configured && (
          <form onSubmit={handleSubmit} aria-label="Formulário de login" noValidate>
            <div className="mb-4">
              <label htmlFor="login-email" className="label">E-mail</label>
              <input
                id="login-email"
                type="email"
                className="input"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>

            <div className="mb-6">
              <label htmlFor="login-password" className="label">Senha</label>
              <input
                id="login-password"
                type="password"
                className="input"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>

            {(formError ?? authError) && (
              <div className="alert alert-danger mb-4" role="alert">
                {formError ?? authError}
              </div>
            )}

            <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
              {submitting ? "Entrando..." : "Entrar"}
            </button>
          </form>
        )}

        <p className="text-xs text-[var(--color-text-muted)] mt-6 text-center">
          Acesso exclusivo para contas autorizadas.
        </p>
      </div>
    </div>
  );
}
